import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import {
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';
import PrimaryButton from './styled/PrimaryButton';

// Initialize Stripe - use Vite env var if available, otherwise fetch from backend
let stripePromise: Promise<any> | null = null;

const getStripePublishableKey = async (): Promise<string> => {
  console.log('[Stripe] 🔍 Checking for Stripe publishable key...');
  
  // First, try to use the Vite environment variable (embedded at build time)
  const viteKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  console.log('[Stripe] 📦 Checking embedded Vite env var:', {
    exists: !!viteKey,
    type: typeof viteKey,
    length: viteKey ? String(viteKey).length : 0,
    preview: viteKey ? `${String(viteKey).substring(0, 20)}...` : 'N/A'
  });
  
  if (viteKey && typeof viteKey === 'string' && viteKey.trim() !== '') {
    const trimmedKey = viteKey.trim();
    console.log('[Stripe] ✅ Using embedded Stripe publishable key from Vite env var');
    return trimmedKey;
  }
  
  console.log('[Stripe] ⚠️  Embedded key not found, falling back to API call');
  console.log('[Stripe] 🌐 Fetching from: /api/stripe-publishable-key');

  // Fallback: fetch from backend API
  try {
    const startTime = Date.now();
    const response = await axios.get('/api/stripe-publishable-key', {
      withCredentials: true
    });
    const duration = Date.now() - startTime;
    
    console.log('[Stripe] 📡 API Response received:', {
      status: response.status,
      duration: `${duration}ms`,
      hasData: !!response.data,
      hasPublishableKey: !!response.data?.publishableKey,
      keyLength: response.data?.publishableKey?.length || 0
    });
    
    if (response.data?.publishableKey) {
      const key = response.data.publishableKey;
      if (key && typeof key === 'string' && key.trim() !== '') {
        console.log('[Stripe] ✅ Successfully retrieved Stripe publishable key from API');
        return key.trim();
      }
      console.error('[Stripe] ❌ Key found but invalid format:', { keyType: typeof key, keyValue: key });
    } else {
      console.error('[Stripe] ❌ Publishable key not found in API response:', response.data);
    }
    throw new Error('Publishable key not found in response');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[Stripe] ❌ API Request failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        responseData: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 503) {
        const errorData = error.response.data;
        console.error('[Stripe] 🔴 Backend configuration error:', errorData);
        if (errorData?.message) {
          throw new Error(errorData.message);
        }
        throw new Error('Stripe publishable key is not configured in the backend. Please ensure VITE_STRIPE_PUBLISHABLE_KEY is set in CI/CD variables.');
      }
      
      if (error.response?.status === 401) {
        console.error('[Stripe] 🔴 Authentication error - endpoint may require auth or CORS issue');
      }
      
      if (error.response?.status === 403) {
        console.error('[Stripe] 🔴 CORS error - origin not allowed');
      }
    } else {
      console.error('[Stripe] ❌ Network/Request error:', error);
    }
    throw new Error('Unable to initialize Stripe. Please contact support.');
  }
};

const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.message || 'Request failed';
  }
  return 'An unexpected error occurred';
};

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(Number.isFinite(value) ? value : 0);

interface StripePaymentProps {
  amount: number;
  loanId: string;
  paymentDate: Date;
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

const StripePayment: React.FC<StripePaymentProps> = ({
  amount,
  loanId,
  paymentDate,
  onSuccess,
  onError,
  onCancel,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [nameOnCard, setNameOnCard] = useState('');
  const [isCardComplete, setIsCardComplete] = useState(false);
  const paymentIntentCreatedRef = useRef<boolean>(false);


  useEffect(() => {
    const initializeStripe = async () => {
      console.log('[Stripe] 🚀 Initializing Stripe payment component...');
      try {
        if (!stripePromise) {
          console.log('[Stripe] 🔑 Fetching publishable key...');
          const publishableKey = await getStripePublishableKey();
          console.log('[Stripe] 📝 Key received:', {
            hasKey: !!publishableKey,
            keyLength: publishableKey?.length || 0,
            keyPreview: publishableKey ? `${publishableKey.substring(0, 20)}...` : 'N/A'
          });
          
          if (!publishableKey || publishableKey.trim() === '') {
            console.error('[Stripe] ❌ Empty key received after fetch');
            throw new Error('Stripe publishable key is not configured. Please contact support.');
          }
          
          console.log('[Stripe] 📦 Loading Stripe.js library...');
          stripePromise = loadStripe(publishableKey);
        }
        
        console.log('[Stripe] ⏳ Waiting for Stripe instance...');
        const stripeInstance = await stripePromise;
        if (stripeInstance) {
          console.log('[Stripe] ✅ Stripe initialized successfully');
          setStripe(stripeInstance);
        } else {
          console.error('[Stripe] ❌ Stripe instance is null');
          throw new Error('Failed to initialize Stripe instance');
        }
      } catch (error) {
        console.error('[Stripe] ❌ Initialization failed:', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        
        let errorMessage = 'Failed to initialize Stripe payment system.';
        if (error instanceof Error) {
          if (error.message.includes('publishable key is not configured')) {
            errorMessage = 'Stripe publishable key is not configured. Please ensure VITE_STRIPE_PUBLISHABLE_KEY is set in CI/CD variables.';
          } else {
            errorMessage = error.message;
          }
        }
        setError(errorMessage);
        onError(errorMessage);
      }
    };
    initializeStripe();
  }, [onError]);

  useEffect(() => {
    const createPaymentIntent = async () => {
      // Prevent duplicate payment intent creation from React.StrictMode
      if (paymentIntentCreatedRef.current) {
        console.log('[Payment Intent] Already created, skipping duplicate call');
        return;
      }
      
      paymentIntentCreatedRef.current = true;
      setLoading(true);
      setError('');
      console.log('Creating payment intent with amount:', amount);

      try {
        const response = await axios.post(
          `/api/create-payment-intent`,
          {
            amount: amount,
            currency: 'gbp',
            metadata: {
              loanId: loanId,
              paymentDate: formatDateToDDMMYYYY(paymentDate),
            },
          },
          {
            withCredentials: true,
          }
        );
        console.log('Payment intent created:', response);
        setClientSecret(response.data.clientSecret);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create payment intent';
        setError(errorMessage);
        onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (amount > 0) {
      createPaymentIntent();
    }
  }, [amount, loanId, paymentDate, onError]);

  useEffect(() => {
    if (stripe && clientSecret) {
      const elementsInstance = stripe.elements({
        clientSecret,
        appearance: {
          theme: theme.palette.mode === 'dark' ? 'night' : 'stripe',
          variables: {
            colorPrimary: theme.palette.primary.main,
            colorBackground: theme.palette.mode === 'dark' ? '#232228' : '#ffffff',
            colorText: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          },
        },
      });

      setElements(elementsInstance);

      const card = elementsInstance.create('card', {
        disableLink: true,
        style: {
          base: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
            fontFamily: "'Libre Franklin', sans-serif",
            color: theme.palette.mode === 'dark' ? '#F6F5FF' : '#1A1A1A',
            iconColor: theme.palette.mode === 'dark' ? '#F6F5FF' : '#1A1A1A',
            '::placeholder': {
              color: theme.palette.mode === 'dark' ? '#7A778A' : '#8F8C9A',
            },
          },
          invalid: {
            color: '#FF5B5B',
            iconColor: '#FF5B5B',
          },
        },
        hidePostalCode: true,
      });

      card.on('change', (event) => {
        setIsCardComplete(!!event.complete);
      });

      card.mount('#card-element');
      setCardElement(card);

      return () => {
        card.unmount();
      };
    }
  }, [stripe, clientSecret, theme]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements || !cardElement) {
      setError('Stripe is not loaded');
      return;
    }

    setLoading(true);
    setIsProcessingPayment(true);
    setError('');

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: nameOnCard.trim(),
          },
        },
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        onError(confirmError.message || 'Payment failed');
        setIsProcessingPayment(false);
      } else if (paymentIntent.status === 'succeeded') {
        // Payment succeeded - now complete the backend processing
        try {
          console.log('Completing payment on backend...');
          await axios.post(
            `/api/complete-payment`,
            {
              paymentIntentId: paymentIntent.id,
              loanId: loanId,
              amount: amount,
              paymentDate: formatDateToDDMMYYYY(paymentDate)
            },
            {
              withCredentials: true
            }
          );
          console.log('Payment completed successfully on backend');

          // Keep the processing blocker visible briefly before showing success.
          setTimeout(() => {
            onSuccess(paymentIntent.id);
          }, 5000);
        } catch (completeError) {
          console.error('Error completing payment on backend:', completeError);
          const errorMessage = formatError(completeError);
          setError(`Payment processed but failed to update loan: ${errorMessage}`);
          setIsProcessingPayment(false);
          // Still call onError but with more specific message
          onError(`Payment succeeded but system update failed. Please contact support with payment ID: ${paymentIntent.id}`);
        }
      }
    } catch (err: unknown) {
      const errorMessage = formatError(err);
      setError(errorMessage);
      onError(errorMessage);
      setIsProcessingPayment(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !clientSecret) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const isNameFilled = nameOnCard.trim().length > 0;
  const canPayNow = !loading && !!stripe && !!elements && isNameFilled && isCardComplete;
  const payNowTooltip = 'Please input the above details to make the payment';

  return (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      {isProcessingPayment && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            px: 2,
          }}
        >
          <Box
            sx={{
              maxWidth: 760,
              width: '100%',
              borderRadius: 2,
              bgcolor: '#fff',
              p: 3,
              boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
              textAlign: 'center',
            }}
          >
            <CircularProgress size={28} sx={{ mb: 2 }} />
            <Typography sx={{ color: '#111', fontWeight: 700, mb: 1 }}>
              Payment is being processed.
            </Typography>
            <Typography sx={{ color: '#111', fontWeight: 500 }}>
              Please DO NOT close this window OR close this Browser until the transaction is completed. This will take a few seconds.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          width: '967px',
          minHeight: '504px',
          flexShrink: 0,
          borderRadius: 3,
          background: theme.palette.mode === 'dark' ? '#15131A' : '#ffffff',
          border: `1px solid ${theme.palette.mode === 'dark' ? '#2F2A38' : '#e0e0e0'}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 18px 36px rgba(0,0,0,0.45)'
            : '0 18px 36px rgba(15,23,42,0.12)',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Libre Franklin', sans-serif",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '100%',
            px: 5,
            py: 4,
            pb: 5,
            '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-input': {
              fontFamily: "'Libre Franklin', sans-serif",
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#0F1D63',
              fontFamily: "'Libre Franklin', sans-serif",
              letterSpacing: '0.04em',
            }}
          >
            stripe
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: -1 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#383347' : '#d6d6e5'}`,
              background: theme.palette.mode === 'dark' ? '#1E1B25' : '#f9f9ff',
              px: 4,
              py: 3,
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#3A3448' : '#dcdced'}`,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 30,
                    borderRadius: 1,
                    background: '#ffffff',
                    border: '1px solid #E3E3F2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#FF5F00',
                      position: 'absolute',
                      left: 10,
                      zIndex: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#FF6600',
                      position: 'absolute',
                      right: 10,
                      zIndex: 2,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 30,
                    borderRadius: 1,
                    background: '#1A1F71',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'white',
                  }}
                >
                  VISA
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.primary }}>
                Name on card
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter cardholder name"
                value={nameOnCard}
                onChange={(e) => setNameOnCard(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 56,
                    background: theme.palette.mode === 'dark' ? '#18161D' : '#ffffff',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: theme.palette.mode === 'dark' ? '#3B3547' : '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.primary }}>
                Card Information
              </Typography>
              <Box
                id="card-element"
                sx={{
                  flex: 1,
                  minHeight: 64,
                  width: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#3B3547' : '#ccc'}`,
                  background: theme.palette.mode === 'dark' ? '#18161D' : '#ffffff',
                  px: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                  },
                  '& .__PrivateStripeElement': {
                    width: '100%',
                  },
                  '& iframe': {
                    color: theme.palette.mode === 'dark' ? '#F6F5FF' : '#1A1A1A',
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Tooltip
              title={!canPayNow ? payNowTooltip : ''}
              arrow
              disableHoverListener={canPayNow}
            >
              <span>
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={!canPayNow}
                  fullWidth>
                  {loading ? <CircularProgress size={20} color="inherit" /> : `Pay Now (${formatAmount(amount)})`}
                </PrimaryButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StripePayment; 